import React from 'react';
import { useTranslation } from 'react-i18next';

import { Form } from '~/components';
import { ButtonProps } from '~/components/Button';
import FormDrawer from '~/components/FormDrawer';
import { useAssignLead } from '~/controllers/agent';
import { mapItemsQueryKey } from '~/controllers/agentMap';
import { useCreateNewSalesLead } from '~/controllers/sales';
import { queryClient } from '~/index';
import LeadForm, { formName } from '~/pages/sales/form/LeadForm';
import { AgentLeadPayload } from '~/types/AgentLead';
import buildNamePath from '~/utils/buildNamePath';
import { isTeamLead } from '~/utils/getDecodedJwt';
import LeadAddForm from '~/pages/sales/form/LeadAddForm';
import { showErrorMessage } from '~/utils/handleError';

interface Props {
  open: boolean;
  onClose: () => void;
  initialValues?: any;
}

export const LeadCreate: React.FC<Props> = (props) => {
  const { t } = useTranslation('agent');
  const { open, onClose, initialValues } = props;
  const createLead = useCreateNewSalesLead();
  const assignLead = useAssignLead();
  const [form] = Form.useForm<AgentLeadPayload>();

  const onCreate = (newValues: any, withCloseDrawer = true) => {
    return createLead
      .mutateAsync({
        ...newValues,
        email: newValues.businessEmail,
        phoneNumber: newValues.businessPhone,
        url: 'https://example.com/onboarding?utm_source=face&utm_medium=social',
        adGroupName: 'signup_button',
        referralUrl: 'example.com',
        landingPage: 'https://example.com',
        campaignName: 'onboarding',
      })
      .then(async (newLead) => {
        if (isTeamLead())
          await assignLead.mutateAsync({
            leadId: newLead.leadId,
            agentId: newValues.assignee?.id ?? '',
          });

        if (withCloseDrawer) onClose();
        form.resetFields();
        await queryClient.invalidateQueries('leads');
        await queryClient.invalidateQueries(mapItemsQueryKey);
        return newLead;
      })
      .catch((e) => {
        const data = e?.response?.data;
        const description = data?.description ?? e?.message ?? 'Something went wrong. Please try again.';
        showErrorMessage(description);
      });
  };

  const createLeadButton = [
    {
      id: 'createLeadButton',
      title: t('Save'),
      type: 'default',
      htmlType: 'submit',
      loading: createLead.isLoading || assignLead.isLoading,
      danger: true,
      form: formName,
    },
  ] as ButtonProps[];

  return (
    <FormDrawer
      id="leadCreate"
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      title={t('New Lead')}
      formName={formName}
      closable
      withCustomButtons
      withContinueButton={false}
      customButtonsProps={createLeadButton}
    >
      <LeadAddForm
        form={form}
        onFinish={onCreate}
        n={buildNamePath([])}
        initialValues={initialValues}
      />
    </FormDrawer>
  );
};
